// ── useStreams: client hook for data stream management ───────
// Wraps the /api/streams/* endpoints.

import { useCallback } from 'react';

export default function useStreams({ sessionId, authFetch }) {
  const attachStream = useCallback(async (nodeId, { type, url, interval }) => {
    const res = await authFetch('/api/streams/attach', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, nodeId, type, url, interval }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Failed to attach stream' }));
      throw new Error(err.error);
    }
    return res.json();
  }, [sessionId, authFetch]);

  const removeStream = useCallback(async (nodeId, streamId) => {
    const res = await authFetch('/api/streams/remove', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, nodeId, streamId }),
    });
    if (!res.ok) throw new Error('Failed to remove stream');
    return res.json();
  }, [sessionId, authFetch]);

  const pauseStream = useCallback(async (nodeId, streamId) => {
    const res = await authFetch('/api/streams/pause', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, nodeId, streamId }),
    });
    if (!res.ok) throw new Error('Failed to pause stream');
    return res.json();
  }, [sessionId, authFetch]);

  const resumeStream = useCallback(async (nodeId, streamId) => {
    const res = await authFetch('/api/streams/resume', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, nodeId, streamId }),
    });
    if (!res.ok) throw new Error('Failed to resume stream');
    return res.json();
  }, [sessionId, authFetch]);

  const setConfidence = useCallback(async (nodeId, confidence) => {
    const res = await authFetch('/api/streams/confidence', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, nodeId, confidence }),
    });
    if (!res.ok) throw new Error('Failed to set confidence');
    return res.json();
  }, [sessionId, authFetch]);

  const getHistory = useCallback(async (nodeId) => {
    const res = await authFetch(`/api/streams/${sessionId}/${nodeId}/history`);
    if (!res.ok) throw new Error('Failed to load stream history');
    return res.json();
  }, [sessionId, authFetch]);

  return { attachStream, removeStream, pauseStream, resumeStream, setConfidence, getHistory };
}
